import * as m from '@staiter/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Tier3SendAction } from '~/components/approvals';
import { Breadcrumbs, PageHeader } from '~/components/data';
import { PeriodCloseBanner } from '~/components/domain';
import { Button } from '~/components/primitives';
import { apiRequest } from '~/lib/api/client';
import { fetchKpnDetail } from '~/lib/api/finance';
import { formatDate } from '~/lib/format';
import shared from './_auth._shared.module.css';

/**
 * S13-FE.6 — KPN-100 document detail page.
 */

export const Route = createFileRoute('/_auth/finance/kpn/$id')({ component: KpnDetailPage });

// F90 пака `tails-closeout` (Ф6): отправка КПН — только заявкой уровня 3 (`Tier3SendAction`).

async function cancelKpn(id: string) {
  const { z } = await import('zod');
  return apiRequest(
    `/finance/regulatory/kpn/${encodeURIComponent(id)}/cancel`,
    z.object({ id: z.string().uuid(), state: z.string() }),
    { method: 'POST', body: {} },
  );
}

function Row({ label, value }: { label: string; value: string | null | undefined }): JSX.Element {
  return (
    <div className={shared.detailRow}>
      <span className={shared.detailRowLabel}>{label}</span>
      <strong>{value ?? '—'}</strong>
    </div>
  );
}

function KpnDetailPage(): JSX.Element {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['finance', 'kpn', id], queryFn: () => fetchKpnDetail(id) });

  const cancelMut = useMutation({
    mutationFn: () => cancelKpn(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['finance', 'kpn'] }),
  });

  const doc = query.data;
  if (!doc) {
    return (
      <div>
        <PageHeader title={m.phase2_finance_kpn_title()} />
        <div>{query.isLoading ? '…' : 'Not found'}</div>
      </div>
    );
  }

  // С-G Р.5: кнопка не прячется — уже отправленный документ даёт причину на месте, прочие
  // состояния (отменён, отклонён) — код сервера из предпросмотра.
  const pushed = doc.state === 'pushed_to_1c';
  const canCancel = doc.state !== 'pushed_to_1c' && doc.state !== 'cancelled';

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: m.phase2_finance_kpn_title(), to: '/finance/kpn' },
              { label: doc.id.slice(0, 8) },
            ]}
          />
        }
        title={`${m.phase2_finance_kpn_title()} · ${doc.id.slice(0, 8)}`}
        actions={
          <>
            <Tier3SendAction
              blockedReason={pushed ? m.tier3_send_reason_already_pushed() : undefined}
              label={m.esf_button_push()}
              subject={{
                taskType: 'regulatory_push_kpn',
                subjectKind: 'regulatory_doc',
                payload: { id },
              }}
            />
            {canCancel && (
              <Button
                variant="ghost"
                onClick={() => cancelMut.mutate()}
                loading={cancelMut.isPending}
              >
                {m.invoice_button_cancel()}
              </Button>
            )}
          </>
        }
      />
      <PeriodCloseBanner scope="regulatory" />
      <div className={shared.detailCard}>
        <Row label={m.esf_column_state()} value={doc.state} />
        <Row
          label={m.esf_column_period()}
          value={`${formatDate(doc.period.date_from)} — ${formatDate(doc.period.date_to)}`}
        />
        <Row label={m.esf_column_onec_ref()} value={doc.onec_doc_ref} />
        <Row label="Locale" value={doc.locale} />
      </div>
    </div>
  );
}
